import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { ChevronDown } from 'react-feather';
import {
  WrenchIcon,
  IdentificationIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import FilePDFIcon from '@/assets/svg/FilePDF.svg';
import FilePNGIcon from '@/assets/svg/FilePNG.svg';
import FileJPGIcon from '@/assets/svg/FileJPG.svg';
import FileJPEGIcon from '@/assets/svg/FileJPEG.svg';
import NoAccessScreen from '../NoAccessScreen';
import { Tooltip } from '@/shared/components/Tooltip';
import { Snackbar } from '@/shared/components/Snackbar';
import {
  listEquipment,
  type EquipmentListItem,
  type EquipmentFileItem,
} from '../../api/listEquipmentApiService';
import { getPresignedUrlsBatch } from '../../api/presignedUrlBatchApiService';
import { useClientId } from '@/shared/hooks/useClientId';

const LIMIT = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

function getDocIcon(filename: string): ReactNode {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'pdf':
      return <FilePDFIcon />;
    case 'png':
      return <FilePNGIcon />;
    case 'jpeg':
      return <FileJPEGIcon />;
    case 'jpg':
      return <FileJPGIcon />;
    default:
      return <FilePDFIcon />;
  }
}

function openFileInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function downloadFile(url: string, filename: string): Promise<void> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      openFileInNewTab(url);
      return;
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    openFileInNewTab(url);
  }
}

// ─── FileRow ──────────────────────────────────────────────────────────────────

interface FileRowProps {
  file: EquipmentFileItem;
  index: number;
  onDownloadStart: () => void;
}

const FileRow = ({ file, index, onDownloadStart }: FileRowProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const resolveUrl = useCallback(async (): Promise<string | null> => {
    if (file.url?.trim()) {
      return file.url.trim();
    }
    if (file.path) {
      const result = await getPresignedUrlsBatch({ paths: [file.path] });
      return result.files[0]?.url ?? null;
    }
    return null;
  }, [file]);

  const handleOpen = useCallback(async () => {
    try {
      const url = await resolveUrl();
      if (url) {
        openFileInNewTab(url);
      }
    } catch {
      // silently fail
    }
  }, [resolveUrl]);

  const handleDownload = useCallback(async () => {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    onDownloadStart();
    try {
      const url = await resolveUrl();
      if (url) {
        await downloadFile(url, file.filename ?? '');
      }
    } catch {
      // silently fail
    } finally {
      setIsDownloading(false);
    }
  }, [file.filename, isDownloading, onDownloadStart, resolveUrl]);

  return (
    <li className="flex items-center justify-between p-2 gap-5 bg-white rounded">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="shrink-0">
          <Tooltip
            content="Wyświetl plik"
            side="bottom"
            align="center"
            contentMaxWidth={110}
            useContentWidth
            forceShow
            triggerTabIndex={-1}
          >
            <button
              type="button"
              onClick={() => void handleOpen()}
              className="group relative shrink-0 p-0 border-0 bg-transparent cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 rounded"
              style={{ width: '40px', height: '40px' }}
              aria-label={`Otwórz ${file.filename ?? ''}`}
              data-testid={`equipment-doc-icon-${index}`}
            >
              {getDocIcon(file.filename ?? '')}
              <div className="absolute inset-y-0 left-0 right-[5px] rounded flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                <EyeIcon className="w-5 h-5 text-white" />
              </div>
            </button>
          </Tooltip>
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <Tooltip
            content={file.filename ?? ''}
            side="bottom"
            align="center"
            contentMaxWidth={500}
            useContentWidth
          >
            <p className="text-[16px] leading-6 tracking-[0.5px] text-[#2A4455] font-normal truncate">
              {file.filename ?? '—'}
            </p>
          </Tooltip>
          <p className="text-[12px] leading-4 tracking-[0.4px] text-[#878B8E] font-normal">
            {file.fileSize != null ? formatFileSize(file.fileSize) : '—'}
          </p>
        </div>
      </div>
      <div className="shrink-0">
        <Tooltip
          content="Pobierz plik"
          side="bottom"
          align="center"
          contentMaxWidth={95}
          useContentWidth
          forceShow
          triggerTabIndex={-1}
        >
          <button
            type="button"
            onClick={() => void handleDownload()}
            disabled={isDownloading}
            className="shrink-0 p-1 rounded border-0 bg-transparent cursor-pointer text-[#474A4C] hover:bg-[#474A4C14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={`Pobierz ${file.filename}`}
            data-testid={`equipment-doc-download-${index}`}
          >
            <ArrowDownTrayIcon className="w-6 h-6" />
          </button>
        </Tooltip>
      </div>
    </li>
  );
};

// ─── EquipmentAccordionCard ───────────────────────────────────────────────────

interface EquipmentAccordionCardProps {
  item: EquipmentListItem;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onDownloadStart: () => void;
}

const EquipmentAccordionCard = ({
  item,
  index,
  expanded,
  onToggle,
  onDownloadStart,
}: EquipmentAccordionCardProps) => {
  const hasImage = !!item.image;
  const hasDocs = (item.docs?.length ?? 0) > 0;
  const imageUrl = item.image?.url ?? null;
  const [imgError, setImgError] = useState(false);

  return (
    <div className="border border-[#DCDDE0] rounded-xl overflow-hidden">
      <div
        className="px-4 py-4 flex items-center cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4 cursor-pointer w-full">
          {imageUrl && !imgError ? (
            <img
              src={imageUrl}
              alt={item.name}
              className="w-10 h-10 rounded-full object-cover shrink-0"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="bg-neutral-90 rounded-full p-2 shrink-0">
              <WrenchIcon className="w-5 h-5 text-on-surface-variant" />
            </div>
          )}

          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <p className="text-bodyMedium text-neutral-10 break-words">
              {item.name}
            </p>
            <div className="flex items-center gap-1">
              <IdentificationIcon className="w-4 h-4 text-outline shrink-0" />
              <span className="text-bodySmall text-outline">
                {item.equipmentId ?? '—'}
              </span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          data-testid={`equipment-accordion-toggle-${index}`}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#474A4C1A] active:bg-[#474A4C14] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] transition-transform duration-200"
          aria-label={`${expanded ? 'Zwiń' : 'Rozwiń'} szczegóły sprzętu ${item.name}`}
          aria-expanded={expanded}
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <ChevronDown size={24} strokeWidth={1.5} color="#474A4C" />
        </button>
      </div>

      {expanded && (
        <div className="px-4 pt-2 pb-4 flex flex-col gap-4">
          <p
            className="text-[#474A4C] font-normal"
            style={{ fontSize: '18px', lineHeight: '26px' }}
          >
            Dane aparatury
          </p>

          {/* Category | Model Name | Serial Number | Date of Production — 2x2 grid */}
          <div
            className="gap-x-8 gap-y-4"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            }}
          >
            <div className="flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Kategoria
              </p>
              <p
                className="text-[#181A1B] break-words"
                data-testid="equipment-category-value"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {item.categoryLabel || '—'}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Nazwa modelu
              </p>
              <p
                className="text-[#181A1B] break-words"
                data-testid="equipment-model-name-value"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {item.modelName || '—'}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Numer seryjny
              </p>
              <p
                className="text-[#181A1B] break-words"
                data-testid="equipment-serial-number-value"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {item.serialNumber || '—'}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Data produkcji
              </p>
              <p
                className="text-[#181A1B]"
                data-testid="equipment-date-of-production-value"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {item.dateOfProduction
                  ? format(new Date(item.dateOfProduction), 'yyyy/MM/dd')
                  : '—'}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-[4px]">
            <p
              className="text-[#474A4C]"
              style={{
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0.4px',
              }}
            >
              Opis
            </p>
            <p
              data-testid="equipment-description-value"
              className="text-[#181A1B] break-words"
              style={{
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.5px',
              }}
            >
              {item.description || '—'}
            </p>
          </div>

          {hasImage && (
            <div className="flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Zdjęcie aparatury
              </p>
              <ul className="flex flex-col gap-2 list-none p-0 m-0">
                {item.image && (
                  <FileRow
                    file={item.image}
                    index={0}
                    onDownloadStart={onDownloadStart}
                  />
                )}
              </ul>
            </div>
          )}

          {hasDocs && (
            <div className="flex flex-col gap-1">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                Dokumenty bezpieczeństwa
              </p>
              <ul className="flex flex-col gap-2 list-none p-0 m-0">
                {item.docs?.map((doc, idx) => (
                  <FileRow
                    key={doc.resourceId}
                    file={doc}
                    index={hasImage ? idx + 1 : idx}
                    onDownloadStart={onDownloadStart}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── FacilityEquipmentAccordionList ───────────────────────────────────────────

interface FacilityEquipmentAccordionListProps {
  facilityId: string;
  onCountResolved?: (count: number) => void;
}

export const FacilityEquipmentAccordionList = ({
  facilityId,
  onCountResolved,
}: FacilityEquipmentAccordionListProps) => {
  const { clientId } = useClientId();
  const [items, setItems] = useState<EquipmentListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showDownloadSnackbar, setShowDownloadSnackbar] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasNextPage(false);
    setExpandedId(null);
    setAccessDenied(false);
  }, [facilityId]);

  useEffect(() => {
    if (!clientId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    listEquipment({ clientId, facilityId, page, limit: LIMIT })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.accessDenied) {
          setAccessDenied(true);
          return;
        }
        if (result.success && result.data) {
          const incoming = result.data.equipment;
          setItems((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
          setHasNextPage(result.data.pagination.hasNextPage);
          if (page === 1) {
            onCountResolved?.(result.data.pagination.totalCount);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, facilityId, page, onCountResolved]);

  if (accessDenied) {
    return (
      <NoAccessScreen message="You don't have access to view equipments." />
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 1 }).map((_, idx) => (
          <div
            key={idx}
            className="border border-[#DCDDE0] rounded-xl px-4 py-3 flex items-center gap-3 animate-pulse"
          >
            <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0" />
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <div className="rounded-xl bg-white p-4">
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            color: '#474A4C',
            fontSize: 14,
            fontFamily: 'Roboto',
            fontWeight: '400',
            lineHeight: '20px',
            letterSpacing: 0.25,
            wordWrap: 'break-word',
          }}
        >
          No equiment added yet.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        {items.map((item, index) => (
          <EquipmentAccordionCard
            key={item.id}
            item={item}
            index={index}
            expanded={expandedId === item.id}
            onToggle={() =>
              setExpandedId((prev) => (prev === item.id ? null : item.id))
            }
            onDownloadStart={() => setShowDownloadSnackbar(true)}
          />
        ))}

        {isLoading &&
          Array.from({ length: 2 }).map((_, idx) => (
            <div
              key={`shimmer-${idx}`}
              className="border border-[#DCDDE0] rounded-xl px-4 py-3 flex items-center gap-3 animate-pulse"
            >
              <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0" />
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/3" />
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
            </div>
          ))}

        {hasNextPage && !isLoading && (
          <button
            type="button"
            onClick={() => setPage((prev) => prev + 1)}
            className="self-center text-sm font-medium text-[#2A4455] underline hover:text-[#223746] cursor-pointer bg-transparent border-0 p-0"
          >
            Załaduj więcej
          </button>
        )}
      </div>

      <Snackbar
        open={showDownloadSnackbar}
        message="Your file is ready. Check your browser if the download doesn't start."
        variant="success"
        onClose={() => setShowDownloadSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};
